import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { SignaturePad } from '../components/SignaturePad';
import { CheckCircle, XCircle, Clock, Download, SealCheck, Warning } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(v, currency = 'EUR') {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  const cur = (currency || 'EUR').toUpperCase();
  const opts = { style: 'currency', currency: cur };
  if (cur === 'XPF') { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
  try { return new Intl.NumberFormat('fr-FR', opts).format(n); } catch { return `${n} ${cur}`; }
}

export default function PublicQuotePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get('paid') === '1';

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/quotes/${token}`);
      setQuote(r.data);
      if (r.data?.client?.name) setSignerName(r.data.client.name);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Devis introuvable');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const handleAccept = async (signatureDataUrl) => {
    if (!signerName.trim()) { toast.error('Indiquez votre nom'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/portal/quotes/${token}/accept`, {
        signature: signatureDataUrl,
        signer_name: signerName.trim(),
      });
      toast.success('Devis accepté — redirection vers le paiement…');
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur acceptation');
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) { toast.error('Motif requis'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/portal/quotes/${token}/decline`, { reason: declineReason.trim() });
      toast.success('Refus enregistré. Merci pour votre retour.');
      load();
      setShowDecline(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5]"><Loader2 className="animate-spin text-[#2D5A4C]" size={32} /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F5] p-6">
        <div className="max-w-md w-full bg-white rounded-xl border border-[#E8E6E1] p-8 text-center">
          <Warning size={40} className="mx-auto text-amber-500 mb-3" />
          <h1 className="text-lg font-semibold text-[#1A2421]">Devis indisponible</h1>
          <p className="text-sm text-[#5E6D66] mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const statusBadge = {
    draft: <Badge className="bg-gray-100 text-gray-700">Brouillon</Badge>,
    sent: <Badge className="bg-blue-100 text-blue-800">Envoyé</Badge>,
    viewed: <Badge className="bg-blue-100 text-blue-800">Consulté</Badge>,
    accepted: <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle size={12} className="mr-1" />Accepté</Badge>,
    declined: <Badge className="bg-red-100 text-red-800"><XCircle size={12} className="mr-1" />Refusé</Badge>,
    expired: <Badge className="bg-amber-100 text-amber-800"><Clock size={12} className="mr-1" />Expiré</Badge>,
  }[quote.status];

  const actionable = ['sent', 'viewed'].includes(quote.status) && !justPaid;

  return (
    <div className="min-h-screen bg-[#F9F8F5] py-10 px-4" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-[#5E6D66]">Objectif SCOP Outremer</p>
          <h1 className="text-2xl font-bold text-[#1A2421] mt-1">Devis {quote.number}</h1>
          <p className="text-sm text-[#5E6D66]">{quote.title}</p>
          <div className="mt-2">{statusBadge}</div>
        </div>

        {justPaid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center" data-testid="paid-banner">
            <CheckCircle size={28} className="mx-auto text-emerald-600 mb-1" />
            <p className="font-semibold text-emerald-800">Paiement confirmé — merci !</p>
            <p className="text-sm text-emerald-700">Votre facture vous sera envoyée par email.</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-[#E8E6E1] overflow-hidden">
          <div className="grid grid-cols-2 gap-4 p-5 border-b border-[#E8E6E1] text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#5E6D66]">Client</p>
              <p className="font-medium">{quote.client?.name}</p>
              <p className="text-xs text-[#5E6D66]">{quote.client?.email}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-[#5E6D66]">Validité</p>
              <p className="font-medium">Jusqu'au {(quote.valid_until || '').slice(0, 10)}</p>
              <p className="text-xs text-[#5E6D66]">Émis le {(quote.issued_at || '').slice(0, 10)}</p>
            </div>
          </div>

          <div className="p-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#5E6D66] border-b border-[#E8E6E1]">
                  <th className="py-2">Description</th>
                  <th className="py-2 text-right">Qté</th>
                  <th className="py-2 text-right">PU HT</th>
                  <th className="py-2 text-right">TVA</th>
                  <th className="py-2 text-right">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {(quote.lines || []).map((ln, i) => (
                  <tr key={i} className="border-b border-[#E8E6E1]">
                    <td className="py-2">{ln.description}</td>
                    <td className="py-2 text-right">{ln.quantity}</td>
                    <td className="py-2 text-right">{fmt(ln.unit_price_ht, quote.currency)}</td>
                    <td className="py-2 text-right">{Math.round((ln.tva_rate || 0) * 10000) / 100}%</td>
                    <td className="py-2 text-right font-medium">{fmt((ln.quantity || 0) * (ln.unit_price_ht || 0), quote.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="text-[#1A2421]">
                  <td colSpan="4" className="py-3 text-right text-sm font-semibold">Total TTC</td>
                  <td className="py-3 text-right text-lg font-bold text-[#2D5A4C]" data-testid="quote-total-ttc">{fmt(quote.total_ttc, quote.currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="px-5 pb-5">
            <a href={`${API}/portal/quotes/${token}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="border-[#E8E6E1]" data-testid="quote-pdf-btn">
                <Download size={14} className="mr-1" /> Télécharger le PDF
              </Button>
            </a>
          </div>
        </div>

        {actionable && (
          <div className="bg-white rounded-xl border border-[#E8E6E1] p-5 space-y-4" data-testid="quote-actions">
            <h2 className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              <SealCheck size={18} className="text-[#2D5A4C]" /> Accepter ce devis
            </h2>
            <div>
              <label className="text-xs text-[#5E6D66]">Votre nom complet</label>
              <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="quote-signer-name" />
            </div>
            <div>
              <label className="text-xs text-[#5E6D66]">Signature (dessinez ci-dessous)</label>
              <div className="mt-1">
                {submitting ? (
                  <div className="flex items-center gap-2 text-sm text-[#5E6D66] py-6 justify-center">
                    <Loader2 size={16} className="animate-spin" /> Redirection vers le paiement sécurisé…
                  </div>
                ) : (
                  <SignaturePad onSave={handleAccept} height={160} />
                )}
              </div>
            </div>
            <p className="text-[10px] text-[#5E6D66]">En signant, vous acceptez ce devis et serez redirigé vers le paiement en ligne sécurisé (Stripe).</p>

            <div className="pt-3 border-t border-[#E8E6E1]">
              {!showDecline ? (
                <button onClick={() => setShowDecline(true)} className="text-xs text-red-600 hover:underline" data-testid="quote-show-decline">
                  Refuser ce devis
                </button>
              ) : (
                <div className="space-y-2">
                  <label className="text-xs text-[#5E6D66]">Motif du refus</label>
                  <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={3} className="border-[#E8E6E1]" data-testid="quote-decline-reason" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={handleDecline} disabled={submitting} data-testid="quote-decline-btn">Confirmer le refus</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowDecline(false)}>Annuler</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {quote.status === 'accepted' && !justPaid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <CheckCircle size={28} className="mx-auto text-emerald-600 mb-1" />
            <p className="font-semibold text-emerald-800">Devis accepté par {quote.accepted_by} le {(quote.accepted_at || '').slice(0, 10)}</p>
          </div>
        )}
        {quote.status === 'declined' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <XCircle size={28} className="mx-auto text-red-600 mb-1" />
            <p className="font-semibold text-red-800">Devis refusé</p>
            {quote.decline_reason && <p className="text-sm text-red-700 mt-1">Motif : {quote.decline_reason}</p>}
          </div>
        )}
        {quote.status === 'expired' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <Clock size={28} className="mx-auto text-amber-600 mb-1" />
            <p className="font-semibold text-amber-800">Ce devis a expiré</p>
            <p className="text-sm text-amber-700">Contactez Objectif SCOP Outremer pour une prolongation.</p>
          </div>
        )}
      </div>
    </div>
  );
}
